import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import dayjs from "dayjs";
import { CheckCircle, Code, Edit, FileText, MoreVertical, Plus, Search, Trash2, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { UpsertOpcs4CodeRule } from "./UpsertOpcs4CodeRule";
import { useOpcs4CodeRules, type Opcs4CodeRule } from "./useOpcs4CodeRules";

export const Opcs4CodeRules = () => {
  const [ruleSearch, setRuleSearch] = useState("");
  const [codeSearch, setCodeSearch] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Opcs4CodeRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState<Opcs4CodeRule | null>(null);

  const debouncedRuleSearch = useDebounce(ruleSearch, 750);
  const debouncedCodeSearch = useDebounce(codeSearch, 300);

  const rulesOptions = useMemo(
    () => ({
      searchQuery: debouncedRuleSearch,
      codeSearchQuery: debouncedCodeSearch,
    }),
    [debouncedRuleSearch, debouncedCodeSearch],
  );

  const {
    data: rules,
    isLoading: isLoadingRules,
    error: rulesError,
    toggleRuleActive,
    upsertRuleMutation,
    deleteRuleMutation,
  } = useOpcs4CodeRules(rulesOptions);

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  if (rulesError) {
    return (
      <div className="text-center py-8">
        <div className="text-destructive">Error loading OPCS-4 code rules. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            OPCS-4 Code Rules
            <div className="flex-1" />
            <Button
              variant="default"
              size="sm"
              className="gap-1"
              onClick={() => {
                setEditingRule(null);
                setEditDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Create Rule
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search rules by name or rule text..."
                  value={ruleSearch}
                  onChange={(e) => setRuleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="relative">
                <Code className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by OPCS-4 code or description..."
                  value={codeSearch}
                  onChange={(e) => setCodeSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Rules Table */}
            {isLoadingRules ? (
              <div className="text-center py-8">Loading rules...</div>
            ) : rules.length > 0 ? (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Rule Text</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Associated Codes</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">{rule.name}</TableCell>
                        <TableCell className="max-w-md">
                          <div className="text-sm text-muted-foreground">{truncateText(rule.rule_text, 150)}</div>
                        </TableCell>
                        <TableCell>
                          {rule.is_active ? (
                            <button
                              onClick={() => toggleRuleActive(rule.id, rule.is_active)}
                              className="inline-flex items-center justify-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer w-20"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleRuleActive(rule.id, rule.is_active)}
                              className="inline-flex items-center justify-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer w-20"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </button>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {rule.opcs4CodeRuleAssignments.length > 0 ? (
                              rule.opcs4CodeRuleAssignments.map((assignment) => (
                                <div key={assignment.id} className="text-sm">
                                  <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                                    {assignment.opcs4Code.code}
                                  </span>
                                  <span className="ml-2 text-muted-foreground">
                                    {truncateText(assignment.opcs4Code.description || "", 50)}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">No codes assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {dayjs(rule.created_at).tz().format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditingRule(rule);
                                  setEditDialogOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setDeletingRule(rule);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {ruleSearch || codeSearch ? "No rules found matching your search." : "No OPCS-4 code rules found."}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <UpsertOpcs4CodeRule
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={() => {
          setEditDialogOpen(false);
          setEditingRule(null);
        }}
        rule={editingRule}
        upsertRule={upsertRuleMutation}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingRule?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deletingRule) {
                  try {
                    await deleteRuleMutation.mutateAsync(deletingRule.id);
                    setDeleteDialogOpen(false);
                    setDeletingRule(null);
                  } catch (error) {
                    // Error is already handled by the mutation
                    console.error("Error deleting rule:", error);
                  }
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteRuleMutation.isPending}
            >
              {deleteRuleMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
